import { useEffect, memo, useMemo, useState } from 'react';
import { Steps, Row, Col, Button, Skeleton } from 'antd';

import { difference, isArray } from 'lodash';

import {
  FormOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';

import { Form, StyledModal, CustomNotification } from 'common/components';
import { FormQasTargetMember, SectionQasRequireField } from './';
import ClaimsVisibility from 'pages/home/ribbon/qas-require-field/components/ClaimsVisibility';

import useSteps from 'pages/home/ribbon/qas-require-field/components/useStepsRequiredField';
import {
  useGetQasRequiredFields,
  useCreateQasRequiredFields,
  useEditQasRequiredFields,
  useSaveClaimsVisibility,
} from 'hooks/qas-require-field/useQasRequireField';
import { useDispatchReloadPage } from 'hooks/useReloadPage';
import { useGetAllProductFields } from 'hooks/qas-require-field/useQasRequireField';
import {
  filterRequiredFieldsByGroupName,
  getAllFieldsByGroupName,
  getAllRequiredFields,
} from 'pages/home/ribbon/qas-require-field/components/utils';

import { checkIsDollarProduct } from 'utils/checkIsOwner';

import './ModalFormQasRequireField.less';

export const STEP_VALUES = {
  SELECT_TARGET_MEMBER: 0,
  SELECT_REQUIRED_PROPERTIES: 1,
  CLAIMS_VISIBILITY: 2,
};

const ModalFormQasRequireField = (props) => {
  const { isEdit, id, isModalOpen, setIsModalOpen } = props;
  const [formInstance] = Form.useForm();
  const [formVisibilityInstance] = Form.useForm();

  const reloadPage = useDispatchReloadPage();
  const createQasRequiredFields = useCreateQasRequiredFields();
  const editQasRequiredFields = useEditQasRequiredFields();
  const claimsVisibilityMutation = useSaveClaimsVisibility();

  const { data, isLoading } = useGetQasRequiredFields(id);
  const { data: productFieldGroups } = useGetAllProductFields();

  const [selectedItems, setSelectedItems] = useState();
  const [requiredProperties, setRequiredProperties] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [formData, setFormData] = useState('');

  const productType = Form.useWatch('productType', formInstance);

  const visibleClaims = useMemo(() => data?.visibleClaims || [], [data]);

  const {
    isRenderClaimsVisibility,
    isExistClaims,
    isTargetMemberStep,
    setStep,
    step,
    onBackStep,
    onNextStep,
  } = useSteps({
    productFieldGroups,
    productType: formData?.productType,
  });

  useEffect(() => {
    if ((isEdit, data)) {
      formInstance.setFieldsValue({
        productType: data?.productType,
        searchText: data?.targetMember,
      });
      setSearchText(data?.targetMember);
      setRequiredProperties(data?.requiredFields);
      setSelectedItems([
        { companyId: data?.targetMemberId, companyName: data?.targetMember },
      ]);
      setFormData({
        productType: data?.productType,
        searchText: data?.targetMember,
      });
    } else {
      formInstance.resetFields();
    }
  }, [isEdit, data, formInstance]);

  useEffect(() => {
    if (isEdit) {
      setStep(STEP_VALUES.SELECT_REQUIRED_PROPERTIES);
    }
  }, [isEdit, setStep]);

  useEffect(() => {
    if (requiredProperties?.length === 0 || !isArray(productFieldGroups)) {
      return;
    }
    let newProductFieldGroups = [];
    let fieldsByProductType = [];
    let fieldsByMemberId = [];
    let newFieldsRequire = [...requiredProperties];

    if (productType === 'HBC / OTC' || productType === 'Non-Food') {
      newProductFieldGroups = productFieldGroups.filter(
        (val) => val?.groupName === 'Ingredients'
      );
    }
    fieldsByProductType = getAllRequiredFields(newProductFieldGroups);
    if (
      selectedItems?.length > 0 &&
      selectedItems[0]?.companyId === process.env.REACT_APP_DOLLAR_GENERAL_ID
    ) {
      const newFields = productFieldGroups.filter(
        (val) => val?.groupName === 'Product Contains'
      );
      fieldsByMemberId = getAllRequiredFields(newFields);
    }
    if (fieldsByProductType?.length > 0) {
      newFieldsRequire = difference(newFieldsRequire, fieldsByProductType);
    }
    if (fieldsByMemberId?.length > 0) {
      newFieldsRequire = difference(newFieldsRequire, fieldsByMemberId);
    }
    setRequiredProperties(newFieldsRequire);
  }, [productType, selectedItems]);

  const onCancel = () => {
    setIsModalOpen(false);
  };

  const handleSetupRequiredFields = async () => {
    const targetMemberId =
      selectedItems?.length > 0 && selectedItems[0]?.companyId;
    const isDollarGeneral = checkIsDollarProduct(targetMemberId);
    let params = {
      targetMemberId: targetMemberId,
      productType: formData?.productType,
      requiredFields: requiredProperties,
    };
    let title = 'Create';
    let qasMutate = createQasRequiredFields;
    if (isEdit) {
      const fieldsByGroupName = getAllFieldsByGroupName(productFieldGroups);
      const requiredFields = filterRequiredFieldsByGroupName(
        fieldsByGroupName,
        requiredProperties,
        formData?.productType,
        isDollarGeneral
      );
      params.id = id;
      params.requiredFields = requiredFields;
      title = 'Edit';
      qasMutate = editQasRequiredFields;
    }

    qasMutate.mutate(params, {
      onSuccess: async (response) => {
        if (response?.isSuccess) {
          CustomNotification.success(`${title} required fields successfully`);
          isExistClaims && setStep(STEP_VALUES.CLAIMS_VISIBILITY);
        } else {
          CustomNotification.error(
            response?.message || `${title} required fields failed! Please try!`
          );
        }
      },
      onError: (error) => {
        CustomNotification.error(
          error?.message || 'Server error! Please try again later!'
        );
      },
    });
  };

  const isEmptySelectedItems = !selectedItems || selectedItems.length === 0;
  const isEmptyRequiredProperties =
    !requiredProperties || requiredProperties.length === 0;

  return (
    <StyledModal
      visible={isModalOpen}
      title={`${isEdit ? 'Update' : 'Create'} QA Specification Required Fields`}
      maskClosable={false}
      onCancel={onCancel}
      width='60vw'
      bodyStyle={{
        height: '80vh',
      }}
      className='qa-spec-require-field__modal'
      footer={
        <>
          {step === STEP_VALUES.SELECT_TARGET_MEMBER && (
            <TargetMemberActions
              onClick={async () => {
                await formInstance.validateFields().then((value) => {
                  if (isTargetMemberStep) {
                    setFormData(value); // prevent setFormData to undefined
                  }
                  onNextStep();
                });
              }}
              isDisabledNextButton={isEmptySelectedItems}
            />
          )}
          {step === STEP_VALUES.SELECT_REQUIRED_PROPERTIES && (
            <RequiredFieldsActions
              isDisabledSaveButton={isEmptyRequiredProperties}
              isLoadingSaveRequiredFields={
                createQasRequiredFields.isLoading ||
                editQasRequiredFields.isLoading
              }
              isExistClaims={isExistClaims}
              onSkip={() => {
                onNextStep();
              }}
              onBack={onBackStep}
              onSetupRequiredFields={handleSetupRequiredFields}
            />
          )}
          {step === STEP_VALUES.CLAIMS_VISIBILITY && (
            <ClaimsActions
              onBack={onBackStep}
              isExistClaims={isExistClaims}
              onSaveClaims={() => {
                const targetMemberId =
                  selectedItems?.length > 0 && selectedItems[0]?.companyId;
                const claims = formVisibilityInstance.getFieldsValue();
                const params = {
                  productType: formData?.productType,
                  memberId: targetMemberId,
                  visibleClaims: isRenderClaimsVisibility
                    ? Object.entries(claims)
                        .filter(([, value]) => value)
                        .map(([key]) => key)
                    : [],
                };

                claimsVisibilityMutation.mutate(params, {
                  onSuccess: (response) => {
                    if (response?.isSuccess) {
                      CustomNotification.success(
                        'Claims visibility saved successfully'
                      );
                      onCancel();
                      reloadPage();
                    } else {
                      CustomNotification.error(
                        response?.message ||
                          'Failed to save claims visibility! Please try!'
                      );
                    }
                  },
                });
              }}
            />
          )}
        </>
      }
    >
      <Skeleton active loading={isEdit && isLoading}>
        <Steps current={step}>
          <Steps.Step title='Select Target Member' />
          <Steps.Step title='Select Required Properties' />
          {isRenderClaimsVisibility && <Steps.Step title='Claims Visibility' />}
        </Steps>
        <Row style={{ marginTop: 16 }}>
          <Col span={24}>
            {step === STEP_VALUES.SELECT_TARGET_MEMBER ? (
              <FormQasTargetMember
                isEdit={isEdit}
                formInstance={formInstance}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                searchText={searchText}
                setSearchText={setSearchText}
              />
            ) : step === STEP_VALUES.SELECT_REQUIRED_PROPERTIES ? (
              <SectionQasRequireField
                isEdit={isEdit}
                formData={formData}
                targetMember={selectedItems}
                requiredProperties={requiredProperties}
                setRequiredProperties={setRequiredProperties}
              />
            ) : (
              <ClaimsVisibility
                formInstance={formVisibilityInstance}
                visibleClaims={visibleClaims}
                selectedItems={requiredProperties || []}
              />
            )}
          </Col>
        </Row>
      </Skeleton>
    </StyledModal>
  );
};

export const TargetMemberActions = ({ isDisabledNextButton, ...props }) => {
  return (
    <Row gutter={[8, 8]} justify='end'>
      <Col>
        <Button
          icon={<ArrowRightOutlined />}
          type='secondary'
          disabled={isDisabledNextButton}
          {...props}
        >
          Next
        </Button>
      </Col>
    </Row>
  );
};

export const RequiredFieldsActions = ({
  isDisabledSaveButton,
  isLoadingSaveRequiredFields,
  onSkip,
  onBack,
  onSetupRequiredFields,
  isExistClaims,
}) => {
  if (isExistClaims) {
    return (
      <Row gutter={[8, 8]}>
        <Col>
          <Button type='link' onClick={onSkip}>
            Skip for now
          </Button>
        </Col>
        <Col flex={1} />
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Back
        </Button>
        <Button
          type='primary'
          disabled={isDisabledSaveButton}
          onClick={onSetupRequiredFields}
          loading={isLoadingSaveRequiredFields}
        >
          Save Fields & Continue
        </Button>
      </Row>
    );
  }

  return (
    <Row gutter={[8, 8]} justify='end'>
      <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
        Back
      </Button>
      <Button
        type='primary'
        disabled={isDisabledSaveButton}
        icon={<FormOutlined />}
      >
        Save
      </Button>
    </Row>
  );
};

export const ClaimsActions = ({ onBack, onSaveClaims }) => {
  return (
    <Row gutter={[8, 8]} justify='end'>
      <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
        Back
      </Button>
      <Button type='primary' onClick={onSaveClaims}>
        Save Claims & Finish
      </Button>
    </Row>
  );
};

export default memo(ModalFormQasRequireField);